import { Button, Col, Modal, Row, Table, TableColumnsType, Typography } from "antd";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";

import { WorkOrderDetail } from "../mockData";
import {
  DocumentLinkEntry,
  SorItem,
  WorkOrderFormValues,
  approachLabel,
  authorityLabelOf,
  displayDate as asDate,
  displayDateTime,
  hrefOf,
  recordToFormValues,
  recordToLinks,
  recordToSorItems,
  workOrderDocuments,
  routingStatusLabel,
  workOrderStatusLabel,
  downloadDocument,
  linksPayload,
  decisionReasons,
} from "../../Constants";
import { WorkflowReviewer, buildWorkOrderBody, workOrderApi, workflowApi } from "../../../../Services";
import { ProjectInformation as ProjectInformationStep } from "../../Onboarding/Steps/ProjectInformation";
import { sorLineColumns } from "../../Onboarding/Steps/SOR";
import { SOR } from "../../Onboarding/Steps/SOR";
import { Field, Section } from "../../Onboarding/Steps/ReviewSubmit";
import { getColoriezedValue } from "../../../../Utils/getColorizedValue";
import { PDFViewer } from "../../../../Components/PDFViewer/PDFViewer";
import { CommentableDocument, CommentsModal } from "./CommentsModal";
import { ReasonModal } from "./ReasonModal";
import { SentSuccessModal } from "./SentSuccessModal";
import { EditLinksModal } from "./EditLinksModal";
import { useHasPanelAccess } from "../../../../Hooks/useHasAccess";
import { useNotification } from "../../../../Hooks/useNotification";
import { hasKeyString } from "../../../../Utils/CommonUtils";
import styles from "../../WorkOrder.module.scss";

interface Props {
  detail: WorkOrderDetail & Record<string, any>;
  /** Reviewers and approvers, as the drilldown loaded them (see WorkOrderDrilldown). */
  routing: WorkflowReviewer[];
  envelopeId?: string;
  /** Re-reads the work order after an action changes its state. */
  onActed?: () => void;
}

/** A Document Link that opens in a new tab; plain "-" when there is none. */
const DocumentLink = ({ url }: { url?: string }) => {
  const href = hrefOf(url);
  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ wordBreak: "break-all" }}
    >
      {url}
    </a>
  ) : (
    <>-</>
  );
};

const noop = () => undefined;

/* Who may change a work order from here — Admin, Approver and Creator, the
   master variants included as on the routes — and the states past which
   there is nothing left to change. */
const EDITOR_ROLES = ["PANEL_ADMIN", "PANEL_MASTER_ADMIN", "PANEL_REVIEWER", "PANEL_MASTER_REVIEWER", "PANEL_CREATOR"];
/* Every field stays editable until the work order is Completed, Closed or
   Expired; from then on only PR No. and the dates Final Work Order needs.
   Cancelled, nothing; nor a draft here — it is edited in the wizard, from the
   list's Edit. */
const LIMITED_EDIT_LABELS = ["Completed", "Closed", "Expired"];
const NO_EDIT_STATUSES = ["CANCELLED", "DRAFT"];
const LIMITED_EDITABLE: (keyof WorkOrderFormValues)[] = ["prNo", "commencementDate", "completionDate"];

/* The first candidate that actually holds something.
 *
 * `??` was the wrong tool here: it only falls through on null and undefined,
 * so a response carrying `sorItems: []` alongside a populated `sorDetails`
 * settled on the empty one and the table read "No data" against a work order
 * that plainly had lines. */
const firstFilled = (...candidates: any[]): any[] =>
  candidates.find((value) => Array.isArray(value) && value.length) ?? [];

/**
 * Detail — Project Information tab.
 *
 * Read-only view of everything captured by the Add Work Order wizard, in the
 * same order the wizard collects it: project fields, SOR lines (with total),
 * uploaded documents, then the reviewer and approver routing tables.
 */
export const ProjectInformation = ({ detail, routing, envelopeId, onActed }: Props) => {
  const navigate = useNavigate();
  const { openNotification } = useNotification();
  const { getPanelUser, hasPanelRole } = useHasPanelAccess();
  const [resendOpen, setResendOpen] = useState(false);
  const [resentOpen, setResentOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentedOn, setCommentedOn] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ name: string; url: string } | null>(null);
  /** The document being downloaded — its icon spins until the file is saved. */
  const [downloading, setDownloading] = useState("");
  /** Which workflow call is in flight — drives the button spinners. */
  const [busy, setBusy] = useState<"" | "approve" | "reject" | "cancel" | "comment" | "resend">("");

  /* Same path-based mode switch ReviewSPA/SPADrilldown use: this component is
     mounted at both /workorder/view/:id and /workorder-approval/view/:id, and
     only the approval route gets the approver action bar. */
  const isApproval = hasKeyString("approval");
  const backTo = isApproval ? "/workorder-approval" : "/workorder";

  /* Reviewers and approvers arrive on one routing list, separated by `role`;
     anything not flagged an approver is treated as a reviewer. */
  const isApprover = (row: WorkflowReviewer) =>
    String(row.role ?? "").toUpperCase().includes("APPROVER");
  const reviewerRows = routing.filter((row) => !isApprover(row));
  const approverRows = routing.filter(isApprover);

  /* A draft has not entered the approval workflow: nobody can approve, reject
     or return it yet, and there is nothing to cancel or resend. Both action
     bars appear once the work order has been submitted. */
  const isDraft = String(detail.status ?? "").toUpperCase() === "DRAFT";

  /* e-POD's edit pattern: the pencil turns Project Details, SOR Details or the
     documents into the same inputs Add Work Order uses, right here, with
     Cancel / Save. One section at a time. */
  const statusCode = String(detail.status ?? "").toUpperCase();

  /* Approve / Reject / Return with Comment are for a step still waiting on the
     signed-in user: once their step is approved or rejected — or the work
     order itself is finished — the bar goes. */
  const myEmail = String(getPanelUser()?.email ?? "").trim().toLowerCase();
  const isDone = (code?: string) =>
    ["APPROVED", "COMPLETED", "REJECTED", "RETURNED", "CANCELLED", "EXPIRED", "VOIDED", "CLOSED"].includes(
      String(code ?? "").trim().toUpperCase()
    );
  const reasons = decisionReasons(detail, routing);
  const isCancelled = [detail.status, detail.overallStatus].some(
    (code) => String(code ?? "").trim().toUpperCase() === "CANCELLED"
  );
  /* Completed (COMPLETED / APPROVED — both read "Completed"): nothing left to
     cancel or resend, so neither button shows. */
  const isCompleted = [statusCode, detail.overallStatus].some((code) => workOrderStatusLabel(code) === "Completed");
  /* Opened from Work Order Approval's Approved tab: already decided, nothing to act on. */
  const fromApproved = new URLSearchParams(window.location.search).get("from") === "approved";
  const canDecide =
    !fromApproved &&
    !isDone(detail.status) &&
    !isDone(detail.overallStatus) &&
    routing.some((row) => String(row.email ?? "").trim().toLowerCase() === myEmail && !isDone(row.status));
  /* Completed covers COMPLETED and APPROVED — both read "Completed". Past
     these, SOR lines and links lock and Project Details narrows to three fields. */
  /* Read off both the record's status and its Overall Status (the one shown on
     the page) — they need not carry the same code. */
  const isLimited = [statusCode, detail.overallStatus].some((code) =>
    LIMITED_EDIT_LABELS.includes(workOrderStatusLabel(code))
  );
  /* Never from Work Order Approval: approvers review the work order there, they
     do not change it. */
  const canEdit = (section: "project" | "sor" | "links") =>
    !isApproval &&
    hasPanelRole(EDITOR_ROLES) &&
    !NO_EDIT_STATUSES.includes(statusCode) &&
    (section === "project" || !isLimited);
  const [editing, setEditing] = useState<"" | "project" | "sor" | "links">("");
  const [isSaving, setIsSaving] = useState(false);
  const [draftSor, setDraftSor] = useState<SorItem[]>([]);
  const [draftLinks, setDraftLinks] = useState<DocumentLinkEntry[]>([]);
  /* Memoised: the step's Formik reinitialises whenever its values change.
     Requester is not stored on the record (and not sent back); like the
     wizard, it falls back to who is signed in so the step validates. */
  const formValues = useMemo(() => {
    const user = getPanelUser();
    return recordToFormValues(detail, undefined, {
      name: user?.panelUserName ?? user?.userName ?? "",
      email: user?.email ?? "",
    });
  }, [detail]);

  const editAction = (section: "project" | "sor" | "links") =>
    canEdit(section) && envelopeId && !editing
      ? () => {
          setDraftSor(recordToSorItems(detail));
          setDraftLinks(recordToLinks(detail));
          setEditing(section);
        }
      : undefined;
  const inlineEdit = { onCancel: () => setEditing(""), isSaving };

  /* /work-order/edit replaces the record, so the whole work order is sent —
     the edited section from the form, everything else as it stands. */
  const save = ({
    values = formValues,
    sorItems = recordToSorItems(detail),
    links = recordToLinks(detail),
  }: {
    values?: WorkOrderFormValues;
    sorItems?: SorItem[];
    links?: DocumentLinkEntry[];
  }) => {
    if (!envelopeId) return;
    setIsSaving(true);
    workOrderApi
      .edit({
        ...buildWorkOrderBody(values, sorItems),
        templateName: detail.templateName ?? "",
        approvalAuthority: detail.approvalAuthority,
        documentLinkDetails: linksPayload(links),
        envelopeId,
      })
      .then(() => {
        openNotification({ title: "Work Order updated", message: "Your changes have been saved.", type: "success" });
        setEditing("");
        onActed?.();
      })
      .catch(notifyFailure("Could not save the work order"))
      .finally(() => setIsSaving(false));
  };



  /* Keyed here rather than via a rowKey callback: antd deprecates the `index`
     argument, and SOR lines have no id of their own on the detail response. */
  const sorRows: any[] = firstFilled(detail.sorDetails, detail.sorItems, detail.sorItemDetails).map(
    (row: any, index: number) => ({ ...row, key: String(row.sorId ?? row.key ?? index) })
  );

  /* Memoised because the list is handed to CommentsModal, which defaults its
     picker off it — a fresh array each render would re-run that effect. */
  const documentRows = useMemo(() => workOrderDocuments(detail), [detail]);

  /* "Additional Document Details" from the wizard: name/link pairs, which the
     API returns under `documentLinkDetails`. They were being read from
     `detail.documentName` / `detail.documentLink` — singular fields that the
     response does not carry, so both always rendered "-". */
  /* The same reader the wizard reopens the record with — the API answers under
     `documentLinkDetails` or `documentLinks`, and only the first was read here,
     so links the wizard showed rendered "-" on this page. */
  const documentLinks = recordToLinks(detail);

  const commentableDocuments: CommentableDocument[] = useMemo(
    () => documentRows.map(({ id, name }: { id?: number; name: string }) => ({ id, name })),
    [documentRows]
  );

  /** documentId the workflow endpoints hang comments and decisions off. */
  const primaryDocumentId = detail.documentId ?? documentRows[0]?.id;

  const notifyFailure = (title: string) => (error: any) =>
    openNotification({ title, message: error?.message ?? "Please try again.", type: "error" });

  const guard = () => {
    if (envelopeId) return true;
    openNotification({
      title: "Work order not loaded",
      message: "Reopen this work order from the list and try again.",
      type: "error",
    });
    return false;
  };

  /* Named in the Approve / Reject confirmations, as e-POD names the contract. */
  const workOrderName = detail.jobTitle || detail.projectTitle || "this";

  const handleApprove = () => {
    if (!guard()) return;
    setBusy("approve");
    workflowApi
      .approve(envelopeId as string)
      .then(() => {
        openNotification({
          title: "Approved",
          message: "The work order has been approved.",
          type: "success",
        });
        navigate(backTo);
      })
      .catch(notifyFailure("Approve failed"))
      .finally(() => setBusy(""));
  };

  const handleReject = (comment: string) => {
    if (!guard()) return;
    setBusy("reject");
    workflowApi
      .reject({
        envelopeId: envelopeId as string,
        documentId: primaryDocumentId ? String(primaryDocumentId) : undefined,
        comment,
      })
      .then(() => {
        setRejectOpen(false);
        openNotification({
          title: "Rejected",
          message: "The work order has been rejected.",
          type: "success",
        });
        navigate(backTo);
      })
      .catch(notifyFailure("Reject failed"))
      .finally(() => setBusy(""));
  };

  /* Resend/Reply used to stop at the confirmation — no request was made, so the
     comment never reached the trail. */
  const handleResend = (comment: string) => {
    if (!guard()) return;
    setBusy("resend");
    workflowApi
      .resend({ envelopeId: envelopeId as string, comment })
      .then(() => {
        setResendOpen(false);
        setResentOpen(true);
        onActed?.();
      })
      .catch(notifyFailure("Resend failed"))
      .finally(() => setBusy(""));
  };

  const handleCancel = (comment: string) => {
    if (!guard()) return;
    setBusy("cancel");
    workflowApi
      .cancel({
        envelopeId: envelopeId as string,
        documentId: primaryDocumentId ? String(primaryDocumentId) : undefined,
        comment,
      })
      .then(() => {
        setCancelOpen(false);
        openNotification({
          title: "Cancelled",
          message: "The work order has been cancelled.",
          type: "success",
        });
        navigate(backTo);
      })
      .catch(notifyFailure("Cancel failed"))
      .finally(() => setBusy(""));
  };

  const handleComment = (document: CommentableDocument | undefined, comment: string) => {
    if (!guard()) return;
    setBusy("comment");
    workflowApi
      .comment({
        envelopeId: envelopeId as string,
        /* No document chosen: a comment on the work order itself. */
        documentId: document ? Number(document.id ?? primaryDocumentId) || undefined : undefined,
        comment,
        commenterRole: getPanelUser()?.currentRole,
      })
      .then(() => {
        setCommentOpen(false);
        setCommentedOn(document?.name ?? "");
        onActed?.();
      })
      .catch(notifyFailure("Comment failed"))
      .finally(() => setBusy(""));
  };

  const sorColumns = sorLineColumns as TableColumnsType<any>;

  /* The routing list names the person `reviewerName` whichever role they hold,
     so both tables share the renderers. No sorting: the chain's order IS the
     meaning of these tables. */
  const nameColumn = {
    title: "Name",
    dataIndex: "reviewerName",
    render: (value: any, row: WorkflowReviewer) => value ?? row.userName ?? "-",
  };
  const emailColumn = {
    title: "Email Address",
    dataIndex: "email",
    render: (value: any, row: WorkflowReviewer) => value ?? row.userEmail ?? "-",
  };
  const orderColumn = { title: "Approving Order", dataIndex: "routingOrder" };
  /* Where each person is in their step, and when they acted — the same two
     columns (and fields) the SPA's own reviewer tables show. */
  const statusColumn = {
    title: "Status",
    dataIndex: "status",
    render: (value: any) => (value ? getColoriezedValue(routingStatusLabel(value).toLowerCase()) : "-"),
  };
  const reviewedAtColumn = {
    title: "Reviewed At",
    dataIndex: "reviewedAt",
    render: (value: any) => displayDateTime(value) || "-",
  };

  const reviewerColumns: TableColumnsType<any> = [nameColumn, emailColumn, orderColumn, statusColumn, reviewedAtColumn];

  const approverColumns: TableColumnsType<any> = [
    { title: "Designation", dataIndex: "designation" },
    nameColumn,
    emailColumn,
    // The approver view drops the ordering column — an approver acts on their
    // own step and the queue position is not theirs to read off this table.
    ...(isApproval ? [] : [orderColumn]),
    statusColumn,
    reviewedAtColumn,
  ];

  /* v2.1 sends the total (rates resolved per region); older records do not,
     so it is summed from the lines. */
  const total = Number(detail.totalAmount ?? sorRows.reduce((sum, item) => sum + (Number(item.amount) || 0), 0)) || 0;
  const authorityLabel = authorityLabelOf(detail, routing);

  return (
    <>
      <Section
        title="Project Information"
        onEdit={editAction("project")}
      >
        {editing === "project" ? (
          <ProjectInformationStep
            values={formValues}
            templates={[]}
            onNext={(values) => save({ values })}
            onSaveDraft={noop}
            /* Subsidiary, Cost Centre, Region, Sub Region, Category, Procurement
               Approach and Vendor are fixed once the work order exists. */
            lockOrgFields
            /* Completed, Closed or Expired: only PR No. and the two dates. */
            editableFields={isLimited ? LIMITED_EDITABLE : undefined}
            inlineEdit={inlineEdit}
          />
        ) : (
        <Row gutter={24}>
          {/* The work order number first, then the Add Work Order inputs in
              their order and with their labels. */}
          <Field label="Work Order No." value={detail.workOrderNo} />
          <Field
            label="Overall Status"
            value={
              detail.overallStatus
                ? getColoriezedValue(workOrderStatusLabel(detail.overallStatus).toLowerCase())
                : "-"
            }
          />
          <Field label="Requester Name" value={detail.requesterName} />
          {/* As e-POD shows it: the reason, once rejected or cancelled. */}
          {reasons.isRejected && <Field label="Reason for Rejection" value={reasons.rejectReason} full />}
          {reasons.isCancelled && <Field label="Reason for Cancellation" value={reasons.cancelReason} full />}
          <Field label="Requester Email" value={detail.requesterEmail} />
          <Field label="Date Request" value={asDate(detail.dateRequest)} />
          <Field label="Job Title" value={detail.jobTitle} />
          <Field label="Subsidiary/Division" value={detail.subsidiary} />
          <Field label="Cost Centre" value={detail.costCentre} />
          <Field label="Department" value={detail.department} />
          <Field label="PR No." value={detail.prNo} />
          <Field label="Background" value={detail.background} full />
          <Field label="Scope of Work" value={detail.scopeOfWork} full />
          <Field label="Budget Status" value={detail.budgetStatus} />
          <Field label="Commencement Date" value={asDate(detail.commencementDate)} />
          <Field label="Completion Date" value={asDate(detail.completionDate)} />
          <Field label="Region" value={detail.region} />
          <Field label="Work Order Contract Category" value={detail.category} />
          <Field label="Sub Region" value={detail.subRegion} />
          <Field label="Procurement Approach" value={approachLabel(detail.procurementApproach)} />
          <Field label="Vendor" value={detail.vendor} />
        </Row>
        )}
      </Section>

      {editing === "sor" ? (
        <SOR
          items={draftSor}
          setItems={setDraftSor}
          category={detail.category}
          region={detail.region}
          onBack={noop}
          onNext={() => save({ sorItems: draftSor })}
          onSaveDraft={noop}
          inlineEdit={inlineEdit}
        />
      ) : (
      <Section
        title="SOR Details"
        onEdit={editAction("sor")}
      >
      <Table
        columns={sorColumns}
        dataSource={sorRows}
        pagination={false}
        rowKey="key"
        tableLayout="fixed"
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={6}>
              <Typography.Text
                strong
                style={{ display: "block", textAlign: "right" }}
              >
                Total Amount
              </Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6}>
              <Typography.Text strong>{total.toLocaleString()}</Typography.Text>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
      </Section>
      )}

      <Section title="Project Documents">
      {documentRows.length === 0 && <Typography.Text type="secondary">-</Typography.Text>}
      {documentRows.map((doc: any) => (
        <div
          className={styles.fileRow}
          key={doc.key}
        >
          <Icon
            icon={doc.type === "pdf" ? "vscode-icons:file-type-pdf2" : "vscode-icons:file-type-image"}
            className={styles.fileIcon}
          />
          <Typography.Text>{doc.name}</Typography.Text>
          <Icon
            icon="akar-icons:eye"
            role="button"
            aria-label={`View ${doc.name}`}
            className={styles.viewIcon}
            onClick={() => setPreview({ name: doc.name, url: doc.url })}
          />
          {downloading === doc.key ? (
            <Icon
              icon="eos-icons:bubble-loading"
              className={styles.viewIcon}
            />
          ) : (
            <Icon
              icon="mdi:tray-arrow-down"
              role="button"
              aria-label={`Download ${doc.name}`}
              className={styles.viewIcon}
              onClick={() => {
                setDownloading(doc.key);
                downloadDocument(doc.url, doc.name).finally(() => setDownloading(""));
              }}
            />
          )}
        </div>
      ))}

      </Section>

      <PDFViewer
        show={!!preview}
        setShow={() => setPreview(null)}
        title={preview?.name ?? ""}
        url={preview?.url ?? ""}
      />

      <Section
        title="Additional Document Link"
        onEdit={editAction("links")}
      >
      {/* Labelled even when nothing was entered — a "-" under each, as e-POD
          shows it. */}
      {(documentLinks.length ? documentLinks : [{}]).map((link: any, index: number) => (
        <Row
          gutter={24}
          key={`${link.documentName ?? ""}-${index}`}
        >
          <Field
            label="Document Name"
            value={link.documentName ?? link.name}
          />
          <Field
            label="Document Link"
            value={<DocumentLink url={link.documentLink ?? link.url} />}
          />
        </Row>
      ))}
      </Section>

      <EditLinksModal
        open={editing === "links"}
        links={draftLinks}
        saving={isSaving}
        onCancel={() => setEditing("")}
        onSave={(links) => save({ links })}
      />

      <Section title="Reviewers Details">
        <Table
          columns={reviewerColumns}
          dataSource={reviewerRows}
          pagination={false}
          rowKey={(row) => String(row.id ?? row.email)}
        />
      </Section>

      <Section title="Approvers">
        <Row gutter={24}>
          <Field label="Approval Authority" value={authorityLabel} />
        </Row>
        <Table
          columns={approverColumns}
          dataSource={approverRows}
          pagination={false}
          rowKey={(row) => String(row.id ?? row.email)}
        />
      </Section>

      {!isDraft && (isApproval ? canDecide : !isCompleted) && (
      <Row
        justify="end"
        gutter={16}
        className={styles.stepActions}
      >
        {isApproval ? (
          <>
            <Col>
              <Button onClick={() => setCommentOpen(true)}>Return with Comment</Button>
            </Col>
            <Col>
              <Button
                danger
                onClick={() => setRejectOpen(true)}
              >
                Reject
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                onClick={() => setApproveOpen(true)}
              >
                Approve
              </Button>
            </Col>
          </>
        ) : (
          <>
            {/* Already cancelled: only Resend/Reply is left. */}
            {!isCancelled && (
              <Col>
                <Button
                  danger
                  onClick={() => setCancelOpen(true)}
                >
                  Cancel
                </Button>
              </Col>
            )}
            <Col>
              <Button
                type="primary"
                onClick={() => setResendOpen(true)}
              >
                Resend/Reply
              </Button>
            </Col>
          </>
        )}
      </Row>
      )}

      {/* e-POD's confirmation: "Are you sure to approve … ?", Cancel / Yes. */}
      <Modal
        open={approveOpen}
        centered
        closable={false}
        maskClosable={false}
        okText="Yes"
        confirmLoading={busy === "approve"}
        onOk={handleApprove}
        onCancel={() => setApproveOpen(false)}
        cancelButtonProps={{ disabled: busy === "approve" }}
      >
        <Typography.Title
          level={5}
          style={{ margin: 0 }}
        >
          Are you sure to approve {workOrderName} work order?
        </Typography.Title>
      </Modal>

      <ReasonModal
        open={rejectOpen}
        /* e-POD's wording: "Are you sure to reject Test_Budget_01 contract?" */
        title={`Are you sure to reject ${workOrderName} work order?`}
        label="Reason for rejection"
        confirmText="Reject with comment"
        danger
        loading={busy === "reject"}
        onCancel={() => setRejectOpen(false)}
        onConfirm={handleReject}
      />

      <ReasonModal
        open={cancelOpen}
        title="Cancel Work Order"
        label="Reason for cancellation"
        confirmText="Cancel with comment"
        danger
        loading={busy === "cancel"}
        onCancel={() => setCancelOpen(false)}
        onConfirm={handleCancel}
      />

      <ReasonModal
        open={resendOpen}
        loading={busy === "resend"}
        onCancel={() => setResendOpen(false)}
        onConfirm={handleResend}
      />

      <SentSuccessModal
        open={resentOpen}
        onClose={() => setResentOpen(false)}
        title="Resent Successful !"
        message="Work order has been resent successfully."
      />

      <CommentsModal
        open={commentOpen}
        documents={commentableDocuments}
        loading={busy === "comment"}
        onCancel={() => setCommentOpen(false)}
        onComment={handleComment}
      />

      <SentSuccessModal
        open={commentedOn !== null}
        onClose={() => setCommentedOn(null)}
        title="Commented!"
        message={commentedOn ? "Commented Successfully on" : "Commented Successfully"}
        highlight={commentedOn || undefined}
      />
    </>
  );
};
